<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import {
  ROLE_KINDS,
  emptyRole,
  roleToForm,
  validateRole,
  rolePayload,
} from '../catalogForms';
import FormField from './FormField.vue';

export default {
  name: 'RoleModal',
  components: { BaseModal, FormField },
  props: {
    role: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      kinds: ROLE_KINDS,
      form: this.role ? roleToForm(this.role) : emptyRole(),
      errors: {},
      isSubmitting: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.role;
    },
  },
  methods: {
    ...mapActions('coLivingCatalog', ['addRole', 'editRole']),
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.FORM_ERRORS.${this.errors[field]}`)
        : '';
    },
    async onSubmit() {
      this.errors = validateRole(this.form, { isEdit: this.isEdit });
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const body = rolePayload(this.form, { isEdit: this.isEdit });
      try {
        if (this.isEdit) {
          await this.editRole({ key: this.role.key, fields: body });
        } else {
          await this.addRole(body);
        }
        useAlert(this.$t('CO_LIVING.ROLES.SAVED'));
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.ROLES.SAVE_FAILED'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.ROLES.EDIT_ROLE')
          : $t('CO_LIVING.ROLES.ADD_ROLE')
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-role-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.ROLES.FIELDS.KEY')"
        for-id="co-living-role-key"
        :error="errorText('key')"
        :hint="$t('CO_LIVING.ROLES.FIELDS.KEY_HINT')"
      >
        <input
          id="co-living-role-key"
          v-model="form.key"
          type="text"
          maxlength="64"
          :disabled="isEdit"
          :class="[fieldClass, errors.key ? 'border-red-500' : '']"
          data-testid="co-living-role-key"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROLES.FIELDS.LABEL')"
        for-id="co-living-role-label"
        :error="errorText('label')"
      >
        <input
          id="co-living-role-label"
          v-model="form.label"
          type="text"
          maxlength="120"
          :class="[fieldClass, errors.label ? 'border-red-500' : '']"
          data-testid="co-living-role-label"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.ROLES.FIELDS.KIND')"
        for-id="co-living-role-kind"
      >
        <select
          id="co-living-role-kind"
          v-model="form.kind"
          :class="selectClass"
          data-testid="co-living-role-kind"
        >
          <option v-for="kind in kinds" :key="kind" :value="kind">
            {{ $t(`CO_LIVING.ROLES.KINDS.${kind.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.active"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-role-active"
        />
        {{ $t('CO_LIVING.COMMON.ACTIVE') }}
      </label>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-role-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
